import type {
  CmmdRuntimeInventoryRuntime,
  EnvironmentId,
  ProjectId,
  ScopedProjectRef,
  ThreadId,
  ThreadStatus,
} from "@cmmd-forge/contracts";

import { decodeCmmdRuntimeThreadWorkspaceMetadata, ProviderKind } from "@cmmd-forge/contracts";
import type { ModelSelection } from "@cmmd-forge/contracts";
import type { Project, SidebarThreadSummary } from "../../types";
import type { SidebarProjectSnapshot, SidebarProjectSourceMetadata } from "./SidebarProjectItem";
import { CMMD_RUNTIME_ENVIRONMENT_PREFIX } from "./cloudProjectsGroup.logic";
import { resolveInventoryThreadLifecycle } from "./inventoryThreadLifecycle";

function runtimeEnvironmentId(runtime: CmmdRuntimeInventoryRuntime): EnvironmentId {
  return (
    runtime.runtime.runtimeKind === "cloud-vm"
      ? `${CMMD_RUNTIME_ENVIRONMENT_PREFIX}${runtime.runtime.id}`
      : runtime.runtime.id
  ) as EnvironmentId;
}

function inventoryProjectKey(runtimeId: EnvironmentId, projectId: string): string {
  return `cmmd-inventory:${runtimeId}:${projectId}`;
}

type InventoryProject = CmmdRuntimeInventoryRuntime["projects"][number];

function normalizeRepositoryIdentity(repository: string | null): string | null {
  const normalized = repository?.trim().toLowerCase() ?? "";
  return normalized === "" ? null : normalized;
}

function repositorySlug(repository: string): string {
  const separatorIndex = repository.lastIndexOf("/");
  const slug = separatorIndex >= 0 ? repository.slice(separatorIndex + 1) : repository;
  return slug || repository;
}

function inventoryProjectGroupKey(project: InventoryProject): string {
  const repository = normalizeRepositoryIdentity(project.githubRepoFullName);
  return repository ? `repo:${repository}` : `project:${project.projectId}`;
}

function groupRuntimeInventoryProjects(
  runtime: CmmdRuntimeInventoryRuntime,
): readonly (readonly InventoryProject[])[] {
  if (runtime.runtime.runtimeKind === "cloud-vm") {
    return runtime.projects.map((project) => [project]);
  }
  const projectsByIdentity = new Map<string, InventoryProject[]>();
  for (const project of runtime.projects) {
    const key = inventoryProjectGroupKey(project);
    const projects = projectsByIdentity.get(key);
    if (projects) projects.push(project);
    else projectsByIdentity.set(key, [project]);
  }
  return [...projectsByIdentity.values()];
}

function inventoryProjectActivityAt(project: InventoryProject): string {
  return project.lastActivityAt ?? project.updatedAt ?? project.publishedAt;
}

function latestInventoryProject(projects: readonly InventoryProject[]): InventoryProject {
  const project = projects.reduce<InventoryProject | null>((latest, candidate) => {
    if (!latest) return candidate;
    return inventoryProjectActivityAt(candidate).localeCompare(inventoryProjectActivityAt(latest)) >
      0
      ? candidate
      : latest;
  }, null);
  if (!project) throw new Error("Cannot project an empty runtime inventory project group");
  return project;
}

function canonicalInventoryProjectName(
  runtime: CmmdRuntimeInventoryRuntime,
  project: InventoryProject,
): string {
  if (runtime.runtime.runtimeKind === "cloud-vm") return project.title;
  const repository = normalizeRepositoryIdentity(project.githubRepoFullName);
  return repository ? repositorySlug(repository) : project.title;
}

function inventoryRuntimeKind(runtime: CmmdRuntimeInventoryRuntime) {
  if (runtime.runtime.runtimeKind === "cloud-vm") {
    return {
      surface: "cloud-official",
      hostKind: "user-cloud-vm",
      workSource: "cloud-vm",
      executionRuntime: "cloud",
      modelRuntime: "cloud",
      linkState: "cloud-only",
    } as const;
  }
  return {
    surface: "desktop-local",
    hostKind: "desktop-local",
    workSource: "desktop-local",
    executionRuntime: "desktop-local",
    modelRuntime: "local",
    linkState: "linked",
  } as const;
}

function inventoryActionPresentation(runtime: CmmdRuntimeInventoryRuntime) {
  const runtimeLabel = runtime.runtime.displayName;
  if (runtime.runtime.runtimeKind === "cloud-vm") {
    return {
      actionAuthority: "inventory-only",
      label: "Cloud inventory",
      description: "This project summary is synced from CMMD runtime inventory.",
      actionRouteLabel: "Runtime inventory",
      actionRouteDescription: "Open/live execution is handled by the owning runtime.",
      actionRouteAvailable: false,
    } as const;
  }
  if (runtime.inventoryStatus === "offline") {
    return {
      actionAuthority: "inventory-only",
      label: "Desktop inventory",
      description: "This runtime is offline. Showing the last inventory CMMD received.",
      actionRouteLabel: `${runtimeLabel} is offline`,
      actionRouteDescription: `Reconnect ${runtimeLabel} before starting new work. Synced tasks remain readable.`,
      actionRouteAvailable: false,
    } as const;
  }
  if (runtime.inventoryStatus === "sync_pending") {
    return {
      actionAuthority: "inventory-only",
      label: "Desktop inventory",
      description: "This runtime has not published its project inventory yet.",
      actionRouteLabel: "Runtime unavailable",
      actionRouteDescription:
        "The owning runtime must publish inventory or reconnect before execution actions are available.",
      actionRouteAvailable: false,
    } as const;
  }
  if (runtime.runtime.status.trim().toLowerCase() !== "online") {
    return {
      actionAuthority: "inventory-only",
      label: "Desktop inventory",
      description: "This project summary is synced from CMMD runtime inventory.",
      actionRouteLabel: "Runtime inventory",
      actionRouteDescription: "Open/live execution is handled by the owning runtime.",
      actionRouteAvailable: false,
    } as const;
  }
  return {
    actionAuthority: "runtime",
    label: runtimeLabel,
    description: `Connect securely to ${runtimeLabel} to execute in this project.`,
    actionRouteLabel: runtimeLabel,
    actionRouteDescription: `New tasks, messages, approvals, terminal, git, and browser actions run on ${runtimeLabel}.`,
    actionRouteAvailable: true,
  } as const;
}

function buildInventorySource(runtime: CmmdRuntimeInventoryRuntime): SidebarProjectSourceMetadata {
  return {
    ...inventoryRuntimeKind(runtime),
    ...inventoryActionPresentation(runtime),
    workspaceIdentity: null,
    hideLocalPath: true,
    requiresPermissionGate: false,
  };
}

function toProjectBase(input: {
  readonly runtime: CmmdRuntimeInventoryRuntime;
  readonly environmentId: EnvironmentId;
  readonly project: CmmdRuntimeInventoryRuntime["projects"][number];
}): Project {
  return {
    id: input.project.projectId as ProjectId,
    environmentId: input.environmentId,
    name: input.project.title,
    cwd: input.project.workspaceRoot ?? input.project.githubRepoFullName ?? input.project.title,
    repositoryIdentity: null,
    // "available" is a claim about the runtime, so only a synced snapshot earns
    // it. Offline and sync_pending are both "we do not know", and saying so is
    // what keeps the row readable without implying it can be worked in.
    availability: input.runtime.inventoryStatus === "synced" ? "available" : "unknown",
    defaultModelSelection: null,
    createdAt: input.project.publishedAt,
    updatedAt: input.project.updatedAt,
    scripts: [],
  };
}

function toThreadStatus(status: string): ThreadStatus {
  if (status === "running") return "running";
  if (status === "failed") return "stuck";
  if (status === "completed") return "ready";
  if (status === "archived") return "archived";
  return "idle";
}

export function projectRuntimeInventoryToSidebarProjects(
  runtimes: readonly CmmdRuntimeInventoryRuntime[],
  currentRegisteredRuntimeId: string | null | undefined,
): readonly SidebarProjectSnapshot[] {
  return runtimes.flatMap((runtime) => {
    if (runtime.runtime.id === currentRegisteredRuntimeId) return [];
    // Only emptiness suppresses a row, and it is the honest test: `projects` is
    // what this function projects from, so with none there is nothing to name.
    //
    // `inventoryStatus` is NOT that test, even though it reads like one.
    // CMMD derives the status purely from whether a snapshot row exists
    // (`resolveInventoryStatus`) while `projects` comes from separate
    // replicated rows, so a runtime whose projects and threads replicated
    // before — or without — its snapshot is `sync_pending` WITH projects.
    // Skipping those hid the project while `projectRuntimeInventoryToSidebarThreads`
    // still emitted their threads, which is how a real thread ended up in the
    // sidebar with no project to name it. The status still has its say: it
    // routes through `inventoryActionPresentation`, which marks the row
    // inventory-only and non-actionable — a branch that was unreachable for as
    // long as this line dropped the runtime before it could be consulted.
    if (runtime.projects.length === 0) return [];
    const environmentId = runtimeEnvironmentId(runtime);
    const source = buildInventorySource(runtime);
    return groupRuntimeInventoryProjects(runtime).map((projects) => {
      const project = latestInventoryProject(projects);
      const memberProjectRefs: readonly ScopedProjectRef[] = projects.map((member) => ({
        environmentId,
        projectId: member.projectId as ProjectId,
      }));
      const activeWorktreeKey = inventoryProjectKey(environmentId, project.projectId);
      const projectBase = toProjectBase({ runtime, environmentId, project });
      return Object.assign(projectBase, {
        name: canonicalInventoryProjectName(runtime, project),
        projectKey: inventoryProjectKey(environmentId, inventoryProjectGroupKey(project)),
        environmentPresence: "remote-only" as const,
        source,
        memberProjectRefs,
        remoteEnvironmentLabels: [runtime.runtime.displayName],
        worktreeMembers: projects.map((member, index) => {
          const projectRef = memberProjectRefs[index];
          if (!projectRef) throw new Error("Runtime inventory project reference is missing");
          return {
            environmentId,
            projectId: member.projectId as ProjectId,
            projectRef,
            cwd: member.workspaceRoot ?? member.githubRepoFullName ?? member.title,
            label: runtime.runtime.displayName,
            primaryLabel: runtime.runtime.displayName,
            namespaceLabel: runtime.inventoryStatus === "offline" ? "Offline" : "Synced",
            environmentRole: runtime.runtime.runtimeKind === "cloud-vm" ? "cloud" : "remote",
            worktreeKey: inventoryProjectKey(environmentId, member.projectId),
          } as const;
        }),
        activeWorktreeKey,
        preConnectCloud: null,
      }) satisfies SidebarProjectSnapshot;
    });
  });
}

/**
 * CMMD publishes the provider as a free string on purpose, so it never rejects
 * an inventory from a runtime that added a provider Forge does not know yet.
 * That means the consumer has to narrow it. Anything unrecognised yields NO
 * selection rather than a cast: an unknown provider has no glyph anyway, and
 * fabricating a `ModelSelection` around it would put invalid state on the row.
 */
function toInventoryModelSelection(
  provider: string | null | undefined,
  model: string | null | undefined,
): { modelSelection?: ModelSelection } {
  if (!provider) return {};
  const known = KNOWN_PROVIDER_KINDS.find((kind) => kind === provider);
  if (known === undefined) return {};
  return { modelSelection: { provider: known, model: model ?? "" } };
}

const KNOWN_PROVIDER_KINDS = ProviderKind.literals;

export function projectRuntimeInventoryToSidebarThreads(
  runtimes: readonly CmmdRuntimeInventoryRuntime[],
  currentRegisteredRuntimeId: string | null | undefined,
): readonly SidebarThreadSummary[] {
  return runtimes.flatMap((runtime) => {
    if (runtime.runtime.id === currentRegisteredRuntimeId) return [];
    const environmentId = runtimeEnvironmentId(runtime);
    return runtime.projects.flatMap((project) =>
      project.threads.map((thread) => {
        const lifecycle = resolveInventoryThreadLifecycle(thread);
        return {
          id: thread.threadId as ThreadId,
          environmentId,
          projectId: project.projectId as ProjectId,
          title: thread.title,
          // Spread so the key is absent rather than `undefined` when the runtime
          // published no provider. A cloud row has no session and no local
          // thread shell, so this is its only source for which agent ran it.
          // A model slug alone cannot be mapped to one without guessing, which
          // would put the wrong agent icon on the row.
          ...toInventoryModelSelection(thread.provider, thread.model),
          interactionMode: "default",
          session: null,
          createdAt: thread.publishedAt,
          archivedAt: thread.status === "archived" ? thread.updatedAt : null,
          archive: thread.archive,
          settledAt: lifecycle.settledAt,
          settledOverride: lifecycle.settledOverride,
          updatedAt: thread.updatedAt,
          latestTurn: null,
          branch: thread.branch,
          worktreePath:
            decodeCmmdRuntimeThreadWorkspaceMetadata(thread.metadata)?.worktreePath ?? null,
          latestUserMessageAt: thread.lastActivityAt,
          hasPendingApprovals: false,
          hasPendingUserInput: false,
          hasActionableProposedPlan: false,
          anchorKind: thread.anchorKind,
          anchorCmmdId: thread.anchorCmmdId,
          kind: thread.kind,
          parentThreadId: thread.parentThreadId as ThreadId | null,
          status: toThreadStatus(thread.status),
          workerRole: thread.workerRole,
        };
      }),
    );
  });
}
